/**
 * The tempest long move (ท่ายาว: ลมพายุ, data/longMoves/breezeSword.ts): one sound per beat key (tp*), played by
 * render/longMove.ts when registered. Self-made synth, no samples. Wuxia register: bamboo creak, wind howl, blade
 * whistle, cloth, a deep ground thump at the finisher. Levels sit with sounds/duelMoves.ts (peaks about 0.3); the
 * finisher is the loudest (about -6 dB peak, like combat.guardBreak).
 */
import { STEEL } from '../kit';
import { registerSfx } from '../registry';
import { chime, note, ROOT_D, whoosh } from '../voices';

registerSfx('tpRageFlare', { label: 'พายุ: รวบลม (ตั้งท่า)', cat: 'combat', gain: 0.6, vary: 0.03, gap: 0.3 }, (k) => {
  // Wind sucked in towards the caster: a rising howl, a bamboo creak, a low qi hum swelling.
  whoosh(k, { dur: 0.8, f0: 300, f1: 1800, f2: 1400, vol: 0.2, q: 3, peak: 0.85, color: 'pink' });
  k.noise({ dur: 0.7, vol: 0.08, attack: 0.5, type: 'highpass', f: 4000 });
  k.tone({ type: 'sawtooth', f: 180, f1: 260, dur: 0.5, vol: 0.05, attack: 0.15, vib: [14, 0.05] });
  k.tone({ type: 'triangle', f: note(ROOT_D, 0) / 2, f1: note(ROOT_D, 0), dur: 0.8, vol: 0.12, attack: 0.5 });
  chime(k, { f: note(ROOT_D, 7) * 2, at: 0.55, vol: 0.06, dur: 0.4 });
});

registerSfx('tpUproot', { label: 'พายุ: ถอนรากไผ่', cat: 'combat', gain: 0.45, vary: 0.04, gap: 0.3 }, (k) => {
  // The grove torn up: a wooden creak and crack, a root-tearing scrape, leaves bursting skyward.
  k.tone({ type: 'sawtooth', f: 150, f1: 420, dur: 0.2, vol: 0.08, attack: 0.02, vib: [30, 0.08] });
  k.click({ f: 1400, at: 0.2, vol: 0.2, q: 4 });
  k.noise({ color: 'brown', dur: 0.3, vol: 0.2, attack: 0.03, type: 'lowpass', f: 700, f1: 250 });
  k.thump({ f0: 170, f1: 55, at: 0.18, dur: 0.16, vol: 0.28 });
  whoosh(k, { at: 0.15, dur: 0.3, f0: 800, f1: 4200, f2: 2600, vol: 0.2, q: 1.6, peak: 0.4 });
});

function pass(k: Parameters<Parameters<typeof registerSfx>[2]>[0], f0: number, f1: number, ringF: number): void {
  // A blade whistling by: a thin falling tone, a band sweep that rises and leaves, a bright cut tick.
  k.tone({ type: 'sine', f: f0, f1, dur: 0.34, vol: 0.06, attack: 0.04, linear: true });
  whoosh(k, { dur: 0.36, f0: f0 * 0.5, f1: f0 * 2, f2: f1, vol: 0.26, q: 2.4, peak: 0.4 });
  k.noise({ at: 0.12, dur: 0.04, vol: 0.2, attack: 0.001, type: 'highpass', f: 4500 });
  k.metal({ f: ringF, at: 0.13, dur: 0.3, vol: 0.07, partials: STEEL });
}

registerSfx('tpPass1', { label: 'พายุ: ผ่านฟัน 1', cat: 'combat', gain: 0.6, vary: 0.04, gap: 0.2 }, (k) => pass(k, 2400, 1000, 1500));
registerSfx('tpPass2', { label: 'พายุ: ผ่านฟัน 2', cat: 'combat', gain: 0.6, vary: 0.04, gap: 0.2 }, (k) => pass(k, 2900, 1200, 1800));
registerSfx('tpPass3', { label: 'พายุ: ผ่านฟัน 3', cat: 'combat', gain: 0.65, vary: 0.04, gap: 0.2 }, (k) => {
  // The third, a closing triangle: the same cut, deeper, with a second ring and a heavier step.
  pass(k, 3400, 1400, 2100);
  k.metal({ f: 1260, at: 0.15, dur: 0.4, vol: 0.06, partials: STEEL });
  k.thump({ f0: 200, f1: 70, at: 0.12, dur: 0.12, vol: 0.2 });
});

registerSfx('tpVortexLoop', { label: 'พายุ: ลมหมุนพาขึ้น', cat: 'combat', gain: 1.3, vary: 0.02, gap: 0.5 }, (k) => {
  // The lift: a howl that turns round and climbs, leaves rattling in it (tremolo), thinning as it rises.
  k.noise({ dur: 0.95, vol: 0.2, attack: 0.3, f: 500, f1: 2200, sweep: 0.8, q: 2.5, trem: [9, 0.45], color: 'pink' });
  k.noise({ at: 0.1, dur: 0.8, vol: 0.1, attack: 0.3, f: 2800, f1: 4800, q: 1.4, trem: [22, 0.6] });
  k.tone({ type: 'sawtooth', f: 200, f1: 520, dur: 0.9, vol: 0.05, attack: 0.3, vib: [6, 0.04] });
});

registerSfx('tpWindDraw', { label: 'พายุ: รวบลมก่อนโฉบ', cat: 'combat', vary: 0.03, gap: 0.3 }, (k) => {
  // Air drawn back into the blade, held: a falling suck-in, a high taut whine, then a breath of silence.
  whoosh(k, { dur: 0.5, f0: 3600, f1: 700, f2: 500, vol: 0.2, q: 2.4, peak: 0.15, color: 'pink' });
  k.tone({ type: 'sine', f: 1500, f1: 2300, dur: 0.45, vol: 0.05, attack: 0.3, vib: [18, 0.02] });
  chime(k, { f: note(ROOT_D, 9) * 4, at: 0.3, vol: 0.05, dur: 0.4 });
});

registerSfx('tpFinisher', { label: 'พายุ: ฟาดลงพื้น (ท่าจบ)', cat: 'combat', gain: 0.21, vary: 0.03, gap: 0.5, preview: 0.7 }, (k) => {
  // The dive's end: a falling blade scream, a ground-shaking thump, bamboo splitting, steel ringing, wind blasting out.
  const crunch = k.shaper(6, k.gain(0.5));
  whoosh(k, { dur: 0.22, f0: 900, f1: 4200, f2: 3600, vol: 0.2, q: 2, peak: 0.95 });
  k.noise({ at: 0.2, dur: 0.05, vol: 0.7, attack: 0.001, type: 'highpass', f: 2600, out: crunch });
  k.thump({ at: 0.2, f0: 110, f1: 32, dur: 0.55, vol: 0.55 });
  k.noise({ at: 0.2, color: 'brown', dur: 0.5, vol: 0.3, type: 'lowpass', f: 900, f1: 120 });
  k.noise({ color: 'crackle', at: 0.21, dur: 0.4, vol: 0.4, attack: 0.002, type: 'highpass', f: 1800 });
  k.metal({ f: 780, at: 0.2, dur: 0.9, vol: 0.12, partials: STEEL });
  whoosh(k, { at: 0.22, dur: 0.55, f0: 400, f1: 2500, f2: 600, vol: 0.2, q: 0.9, peak: 0.2, color: 'pink' });
});

registerSfx('tpLeafRain', { label: 'พายุ: ใบไผ่ร่วง', cat: 'combat', vary: 0.05, gap: 0.5 }, (k) => {
  // Leaves settling: papery flutters thinning out, one late bamboo knock, a body's last scuff.
  for (let n = 0; n < 7; n++) {
    k.noise({ at: n * 0.07 + k.range(0, 0.04), dur: 0.12, vol: 0.09 * (1 - n * 0.1), attack: 0.02, type: 'bandpass', f: k.range(3500, 6500), q: 1.5 });
  }
  k.noise({ dur: 0.5, vol: 0.07, attack: 0.15, type: 'highpass', f: 3000, color: 'pink' });
  k.thump({ f0: 300, f1: 150, at: 0.35, dur: 0.06, vol: 0.12 });
});

registerSfx('tpSheathe', { label: 'พายุ: เก็บกระบี่ลมสงบ', cat: 'combat', vary: 0.03, gap: 0.3 }, (k) => {
  // The wind gone still: a long exhale, a sword sliding home, a soft click and one clear bell.
  whoosh(k, { dur: 0.5, f0: 1600, f1: 500, f2: 400, vol: 0.1, q: 1.2, peak: 0.1, color: 'pink' });
  k.noise({ at: 0.1, dur: 0.3, vol: 0.1, attack: 0.05, f: 4200, f1: 2400, q: 3 });
  k.click({ f: 2200, at: 0.42, vol: 0.14, q: 4 });
  k.metal({ f: 1700, at: 0.42, dur: 0.35, vol: 0.05, partials: STEEL });
  chime(k, { f: note(ROOT_D, 7) * 4, at: 0.46, vol: 0.07, dur: 0.7 });
});

registerSfx('tpSpent', { label: 'พายุ: พลาด หมดแรง', cat: 'combat', gain: 0.8, vary: 0.04, gap: 0.3 }, (k) => {
  // The storm whiffed: the wind dies in a falling sigh, a dull step, a loose scatter of leaves, a flat slack tone.
  whoosh(k, { dur: 0.7, f0: 2400, f1: 400, f2: 250, vol: 0.18, q: 1.3, peak: 0.1, color: 'pink' });
  k.thump({ f0: 130, f1: 55, at: 0.3, dur: 0.18, vol: 0.2 });
  k.tone({ type: 'triangle', f: 330, f1: 160, dur: 0.5, vol: 0.06, attack: 0.02 });
  for (let n = 0; n < 4; n++) k.noise({ at: 0.35 + n * 0.08, dur: 0.1, vol: 0.05, type: 'bandpass', f: k.range(3000, 5500), q: 1.5 });
});
